import { ComponentType, memo, PropsWithChildren, useEffect } from 'react';

import { injectIntl, InjectedIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { PopupMenuGroup } from '@atlaskit/menu';
import { Stack, Text } from '@atlaskit/primitives';
import { type ThemeColorModes } from '@atlaskit/tokens';

import { ToggleNav4On } from 'src/app/nav-system/top-nav/feature-toggles';

import { AnalyticsEvent } from '../types';
import urls from '../urls';

import { AccountMenuWorkspaces } from './account-menu-workspaces';
import messages from './account-menu.i18n';
import {
  NonInteractiveDropdownItem,
  ProfileWrapper,
  AvatarWrapper,
} from './account-menu.style';
import { DropdownItem, DropdownItemGroup } from './dropdown-helpers';
import { ThemeSelector } from './theme-selector/theme-selector';
import { handleGlobalThemeChange } from './theme-selector/utils';

export type AccountMenuCurrentUser = {
  is_staff?: boolean;
  account_id?: string;
  uuid?: string;
  extra?: { has_workspace?: boolean };
};

export type AccountMenuUserProps = {
  loginUrl?: string;
  logoutUrl?: string;
  switchAccountUrl?: string;
  manageAccountUrl?: string;
  userEmail?: string | null;
  userAvatarUrl: string | undefined;
  userDisplayName: string | undefined;
  userUuid: string | undefined;
  userWorkspaceId: string | undefined;
  currentUser?: AccountMenuCurrentUser;
  workspaceLinkComponent?: ComponentType<PropsWithChildren<any>>;
  isOrgAdmin?: boolean;
  workspaceOrgId?: string;
};

export type AccountMenuThemeSelectorProps = {
  onThemeSelect?: () => void;
};

export type AccountMenuProps = AccountMenuUserProps & {
  recentlyViewedWorkspacesKeys?: string[];
  publishUiEvent: (event: AnalyticsEvent) => void;
};

type Props = AccountMenuProps & {
  intl: InjectedIntl;
  themeSelectorProps: AccountMenuThemeSelectorProps;
  setHorizontalProfilePopupClose?: () => void;
};

// this is the account menu for nav v3. The account menu for nav v4 (bbc_nav_v4) lives in
// nav-system/top-nav/profile.tsx (uses the new ATL account menu so it's a bit different)
const AccountMenu = memo((props: Props): any => {
  const { publishUiEvent, switchAccountUrl } = props;

  useEffect(() => {
    publishUiEvent({
      action: 'opened',
      actionSubject: 'menu',
      actionSubjectId: 'accountMenu',
      source: 'navigation',
      attributes: { isSwitchAccountLinkEnabled: !!switchAccountUrl },
    });
  }, [publishUiEvent, switchAccountUrl]);

  const handleSwitchAccountClick = () => {
    props.publishUiEvent({
      action: 'clicked',
      actionSubject: 'link',
      actionSubjectId: 'switchAccountLink',
      source: 'navigation',
    });
  };

  const getAnonymousMenuItems = () => {
    const { intl } = props;

    return (
      <PopupMenuGroup minWidth={234} maxWidth={300}>
        <DropdownItem href={urls.ui.login()} key="login-link">
          {intl.formatMessage(messages.logInLink)}
        </DropdownItem>
        <DropdownItem href={urls.ui.signup()} key="signup-link">
          {intl.formatMessage(messages.signUpLink)}
        </DropdownItem>
      </PopupMenuGroup>
    );
  };

  const getAuthenticatedMenuItems = () => {
    const {
      loginUrl,
      logoutUrl,
      manageAccountUrl,
      userAvatarUrl,
      userWorkspaceId,
      userDisplayName,
      userEmail,
      intl,
      recentlyViewedWorkspacesKeys,
      workspaceLinkComponent,
      themeSelectorProps,
      setHorizontalProfilePopupClose,
    } = props;

    if (!userDisplayName) {
      return [];
    }

    const workspaceSlug = userWorkspaceId;

    const { onThemeSelect } = themeSelectorProps;

    return (
      <PopupMenuGroup maxWidth={300}>
        {loginUrl && userEmail && (
          <DropdownItemGroup title={intl.formatMessage(messages.accountHeader)}>
            <NonInteractiveDropdownItem shouldTitleWrap>
              <ProfileWrapper>
                <AvatarWrapper>
                  <Avatar
                    appearance="circle"
                    size="medium"
                    src={userAvatarUrl}
                  />
                </AvatarWrapper>
                <Stack>
                  <Text>{userDisplayName}</Text>
                  <Text size="small">{userEmail}</Text>
                </Stack>
              </ProfileWrapper>
            </NonInteractiveDropdownItem>
            {switchAccountUrl && (
              <DropdownItem
                href={switchAccountUrl}
                onClick={handleSwitchAccountClick}
              >
                {intl.formatMessage(messages.switchAccountLink)}
              </DropdownItem>
            )}
            <ThemeSelector
              onThemeChange={(value: ThemeColorModes) => {
                handleGlobalThemeChange(value);
                onThemeSelect?.();
              }}
              isBeta
            />
          </DropdownItemGroup>
        )}
        {/* this handles checking the FF and returns null if user can't toggle nav UX */}
        <ToggleNav4On />
        {recentlyViewedWorkspacesKeys && (
          <AccountMenuWorkspaces
            key="workspaces"
            userWorkspaceSlug={workspaceSlug}
            recentlyViewedWorkspacesUuids={recentlyViewedWorkspacesKeys}
            workspaceLinkComponent={workspaceLinkComponent}
            hasSeparator={!!manageAccountUrl}
            setHorizontalProfilePopupClose={setHorizontalProfilePopupClose}
          />
        )}

        {logoutUrl && (
          <DropdownItemGroup key="authenticated-user-group" hasSeparator>
            <DropdownItem href={logoutUrl}>
              {intl.formatMessage(messages.logOutLink)}
            </DropdownItem>
          </DropdownItemGroup>
        )}
      </PopupMenuGroup>
    );
  };

  return props.userDisplayName !== undefined
    ? getAuthenticatedMenuItems()
    : getAnonymousMenuItems();
});

export default injectIntl(AccountMenu);
